import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpenCheck, Check, FileText, Pill as PillIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading, StateBlock } from "@/components/common";
import { ActionButton, Card, CardMeta, CardTitle, PageHero, Section, StatusBadge } from "./PageShell";
import { usePatientScopedData, useSelectedPatient } from "./patientContext";

const AccessLost = ({ testId, icon, description }) => (
  <StateBlock testId={testId} icon={icon} title="Acesso indisponível" description={description} />
);

export const ApoioBulletins = () => {
  const { api } = useAuth();
  const { selectedPatient, loadPatients } = useSelectedPatient();
  const { data, blocked, reload } = usePatientScopedData("/bulletins");
  const [params] = useSearchParams();
  const targetId = params.get("b");

  // Aberto por uma notificação: o acesso já foi revalidado pela API; aqui apenas destacamos o item.
  useEffect(() => {
    if (!targetId || !data?.items) return;
    const found = data.items.some((b) => b.id === targetId);
    if (!found) {
      toast.info("Este boletim não está disponível para o paciente selecionado.");
      return;
    }
    document.querySelector(`[data-testid="apoio-bulletin-${targetId}"]`)?.scrollIntoView({ block: "center" });
  }, [targetId, data]);

  const markRead = async (id) => {
    try {
      await api.post("/network/read-receipts", { entity_type: "bulletin", entity_id: id });
      toast.success("Boletim marcado como lido");
      await reload();
      await loadPatients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (blocked) return <AccessLost testId="apoio-bulletins-blocked" icon={BookOpenCheck} description={blocked} />;
  if (data === null) return <Loading label="Carregando boletins…" />;

  const unread = data.items.filter((b) => !b.read).length;

  return (
    <div data-testid="apoio-bulletins-page" className="space-y-8">
      <PageHero
        testId="apoio-bulletins-hero"
        kicker="Boletins publicados"
        title="Boletins"
        subtitle={`Atualizações da equipe clínica sobre ${data.patient_name || selectedPatient?.patient_name}.`}
        banner="boletins"
      />

      <p data-testid="apoio-bulletins-patient" className="sr-only">
        {data.patient_name || selectedPatient?.patient_name}
      </p>

      <Section
        label="Atualizações da equipe"
        hint={unread ? `${unread} não lido(s)` : "Tudo lido"}
      >
        {data.items.length === 0 ? (
          <StateBlock
            testId="apoio-bulletins-empty"
            icon={FileText}
            title="Nenhum boletim publicado ainda"
            description="Você será avisada aqui quando a equipe publicar uma atualização deste paciente."
          />
        ) : (
          data.items.map((b) => (
            <Card key={b.id} testId={`apoio-bulletin-${b.id}`} tone={b.read ? "default" : "highlight"}>
              <div className="flex items-start justify-between gap-3">
                <CardTitle>{b.title}</CardTitle>
                <StatusBadge read={b.read} testId={`apoio-bulletin-read-${b.id}`} />
              </div>
              {b.source_label ? (
                <p data-testid={`apoio-bulletin-source-${b.id}`} className="mt-2 text-[13px] font-semibold text-sage-700">
                  Fonte: {b.source_label}
                </p>
              ) : null}
              {b.correction_notice ? (
                <p className="mt-2.5 rounded-xl bg-warning/15 px-3.5 py-2.5 text-[13px] leading-relaxed text-ink-700">
                  {b.correction_notice}
                </p>
              ) : null}
              <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-ink-600">{b.body}</p>
              <CardMeta>
                <span className="mt-3.5 block">Publicado em {formatDate(b.published_at)}</span>
              </CardMeta>
              {!b.read ? (
                <ActionButton
                  icon={Check}
                  data-testid={`apoio-bulletin-mark-${b.id}`}
                  onClick={() => markRead(b.id)}
                  className="mt-4 w-full sm:w-auto"
                >
                  Marcar como lido
                </ActionButton>
              ) : null}
            </Card>
          ))
        )}
      </Section>
    </div>
  );
};

export const ApoioMedications = () => {
  const { api } = useAuth();
  const { selectedPatient, loadPatients } = useSelectedPatient();
  const { data, blocked, reload } = usePatientScopedData("/medications");

  const markRead = async (id) => {
    try {
      await api.post("/network/read-receipts", { entity_type: "medication_event", entity_id: id });
      toast.success("Comunicado marcado como lido");
      await reload();
      await loadPatients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (blocked) return <AccessLost testId="apoio-medications-blocked" icon={PillIcon} description={blocked} />;
  if (data === null) return <Loading label="Carregando medicamentos…" />;

  const unread = data.events.filter((ev) => !ev.read).length;

  return (
    <div data-testid="apoio-medications-page" className="space-y-8">
      <PageHero
        testId="apoio-medications-hero"
        kicker="Rotina de tratamento"
        title="Medicamentos"
        subtitle={`Medicação em uso e comunicados de ajuste de ${data.patient_name || selectedPatient?.patient_name}.`}
        banner="medicamentos"
      />

      <p data-testid="apoio-medications-patient" className="sr-only">
        {data.patient_name || selectedPatient?.patient_name}
      </p>

      {data.medications.length === 0 && data.events.length === 0 ? (
        <StateBlock
          testId="apoio-medications-empty"
          icon={PillIcon}
          title="Nenhum medicamento por aqui ainda"
          description="Quando a equipe registrar medicamentos ou alterações para este paciente, eles aparecem nesta tela."
        />
      ) : (
        <>
          {data.medications.length ? (
            <Section
              label="Medicação atual"
              hint={`${data.medications.length} item(ns)`}
              testId="apoio-medications-current"
            >
              <Card className="divide-y divide-border p-0">
                {data.medications.map((m) => (
                  <div key={m.id} data-testid={`apoio-medication-${m.id}`} className="flex items-start gap-3.5 p-5">
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sage-50 text-sage-700">
                      <PillIcon size={17} aria-hidden="true" />
                    </span>
                    <div>
                      <CardTitle>{m.name}</CardTitle>
                      <p className="mt-1 text-[14.5px] leading-relaxed text-ink-600">
                        {m.dosage} · {m.schedule}
                      </p>
                    </div>
                  </div>
                ))}
              </Card>
            </Section>
          ) : null}

          {data.events.length ? (
            <Section
              label="Comunicados e ajustes"
              hint={unread ? `${unread} não lido(s)` : "Tudo lido"}
              testId="apoio-medications-events"
            >
              {data.events.map((ev) => (
                <Card key={ev.id} testId={`apoio-med-event-${ev.id}`} tone={ev.read ? "default" : "highlight"}>
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle>{ev.description}</CardTitle>
                    <StatusBadge read={ev.read} testId={`apoio-med-event-read-${ev.id}`} />
                  </div>
                  <CardMeta>
                    <span className="mt-2 block">
                      {ev.medication_name} · {formatDate(ev.created_at)}
                    </span>
                  </CardMeta>
                  {!ev.read ? (
                    <ActionButton
                      icon={Check}
                      data-testid={`apoio-med-event-mark-${ev.id}`}
                      onClick={() => markRead(ev.id)}
                      className="mt-4 w-full sm:w-auto"
                    >
                      Marcar como lido
                    </ActionButton>
                  ) : null}
                </Card>
              ))}
            </Section>
          ) : null}
        </>
      )}
    </div>
  );
};
